import { lazy, Suspense, useEffect, useState } from "react";

/**
 * The booking dialog brings the calendar, the form library and the validation library with it
 * (about a fifth of the main script). This wrapper keeps all of that out of the first download:
 * the real dialog is fetched when a visitor opens it for the first time, and quietly in the
 * background a few seconds after the page is idle, so the first click still feels instant.
 */
const BookingDialogImpl = lazy(() =>
  import("./BookingDialog").then((m) => ({ default: m.BookingDialog })),
);

interface BookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

let warmed = false;
function warmUp() {
  if (warmed || typeof window === "undefined") return;
  warmed = true;
  const load = () => void import("./BookingDialog");
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(load, { timeout: 5000 });
  } else {
    setTimeout(load, 3000);
  }
}

export const BookingDialog = ({ open, onOpenChange }: BookingDialogProps) => {
  const [loaded, setLoaded] = useState(open);

  useEffect(() => {
    if (open) setLoaded(true);
  }, [open]);

  useEffect(() => {
    warmUp();
  }, []);

  if (!loaded) return null;

  return (
    <Suspense fallback={null}>
      <BookingDialogImpl open={open} onOpenChange={onOpenChange} />
    </Suspense>
  );
};
